import Constants from 'expo-constants';
import { useEffect } from 'react';

import { useAuthContext } from '@fhs/auth/src/hooks';
import { useUserAccountsQuery } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { ISOs } from '@fhs-legacy/frontend/src/utils/form/constants';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { usePaymentMethodStore } from '../../../../payment';

import { useApplePayAvailability, useGooglePayAvailability } from './digital-wallet-availability';

export default function useAvailablePaymentMethods() {
  const { user } = useAuthContext();
  const setUserAccountsData = usePaymentMethodStore(s => s.setUserAccountsData);
  const currentUserAccountsData = usePaymentMethodStore(s => s.userAccountsData);

  const canUseGiftCards = useFlag('enable-fhs-gift-cards');
  const canUseApplePay = useApplePayAvailability();
  const canUseGooglePay = useGooglePayAvailability();

  // User's default payment method (set on payment methods page)
  const defaultAccountIdentifier = user?.details?.defaultAccountIdentifier;

  const feAlpha3CountryCode = Constants.expoConfig?.extra?.eas.buildRegion === 'US' ? 'USA' : 'CAN';

  // uses cache from apollo to obtain the userAccounts data again, if not loads with skeleton
  const { data: userAccountsData, loading: userAccountsLoading } = useUserAccountsQuery({
    variables: { feCountryCode: feAlpha3CountryCode as ISOs },
    fetchPolicy: 'cache-and-network',
    skip: (currentUserAccountsData?.userAccounts?.accounts?.length ?? 0) > 0,
  });

  useEffect(() => {
    if (!userAccountsData) return;

    if (userAccountsData !== currentUserAccountsData) {
      setUserAccountsData(userAccountsData, defaultAccountIdentifier, {
        canUseApplePay,
        canUseGooglePay,
      });
    }
  }, [
    userAccountsData,
    currentUserAccountsData,
    setUserAccountsData,
    defaultAccountIdentifier,
    canUseApplePay,
    canUseGooglePay,
  ]);

  return {
    canUseGiftCards,
    canUseApplePay,
    canUseGooglePay,
    userAccountsLoading,
  };
}
